import { RoleIndex } from "./role";
import { UserType } from "./userType";
import { Program } from "./program";

// Mirrors the rail. Adding a destination is a data change here, never a new
// conditional in JSX.
export interface NavItem {
  id: string;
  label: string;
  /** @mui/icons-material component name, resolved by the rail. */
  icon: string;
  /** Roles allowed to see this item. Omit for "everyone in this program". */
  roles?: number[];
  /** Client user types allowed to see this item. Omit for all. */
  userTypes?: number[];
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

/** Three archetypes, not six roles. Role selects the archetype; it does not fork the shell. */
export type ShellArchetype = "portfolio" | "worklist" | "roster";

export const PROGRAM_NAV: Record<number, NavGroup> = {
  [Program.Index.Analytics]: {
    id: "analytics",
    label: "Analytics",
    items: [
      { id: "dashboard", label: "Dashboard", icon: "InsightsOutlined" },
      { id: "growthForecast", label: "Growth & Forecast", icon: "TrendingUpOutlined" },
      { id: "transactions", label: "Transactions", icon: "ReceiptLongOutlined" },
      { id: "salesmarketing", label: "Sales & Marketing", icon: "CampaignOutlined" }
    ]
  },
  [Program.Index.Training]: {
    id: "training",
    label: "Training",
    items: [
      { id: "user_grades", label: "Student grades", icon: "SchoolOutlined" },
      {
        id: "users",
        label: "User management",
        icon: "GroupOutlined",
        userTypes: [UserType.Index.LWS_ADMIN]
      }
    ]
  },
};

/**
 * Sales reps get a worklist, not a KPI grid. Same shell, different primary object.
 *
 * One item on purpose: salesRepView.tsx is still a single screen stacking all
 * five sections (rankings, gain/lost, retention leads...) and does not read the
 * rail selection, so splitting this into accounts/leads/rankings would put three
 * dead buttons in the rail. screen-inventory.md marks that view `open` — it has
 * no design yet — so the split waits for the rebuild rather than being guessed.
 */
export const SALES_REP_NAV: NavGroup = {
  id: "myWork",
  label: "My work",
  items: [{ id: "accounts", label: "My accounts", icon: "FolderSharedOutlined" }]
};

/** Gating transcribed from systemManagement.tsx. Client users never reach the route. */
export const SYSTEM_NAV: NavGroup = {
  id: "system",
  label: "System",
  items: [
    { id: "user", label: "User Management", icon: "GroupOutlined" },
    { id: "client", label: "Client Management", icon: "BusinessOutlined", roles: [RoleIndex.Super] },
    { id: "subscription", label: "Product Management", icon: "Inventory2Outlined", roles: [RoleIndex.Super] },
    { id: "sales_rep", label: "Sales Rep Management", icon: "BadgeOutlined" },
    { id: "client_upload", label: "Client Data Upload", icon: "UploadFileOutlined" }
  ]
};

export const SYSTEM_ROLES: number[] = [RoleIndex.Super, RoleIndex.Admin, RoleIndex.AccountAdmin];
